import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/shared/lib/utils";

/**
 * Renders a markdown string with GitHub-flavored extensions (tables,
 * task lists, strikethrough, autolinks). Tailwind classes are passed
 * per element instead of pulling in `@tailwindcss/typography` so the
 * bundle stays lean.
 *
 * `react-markdown` does NOT render raw HTML by default — `<script>`
 * or `<img onerror>` in the markdown source comes out as literal
 * text. No DOMPurify needed.
 */
interface MarkdownPreviewProps {
  readonly value: string;
  readonly emptyPlaceholder: string;
  readonly className?: string;
  readonly "data-testid"?: string;
}

export function MarkdownPreview({
  value,
  emptyPlaceholder,
  className,
  "data-testid": testId,
}: MarkdownPreviewProps) {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return (
      <div
        data-testid={testId}
        className={cn(
          "rounded-md border border-input bg-background p-3 text-sm text-muted-foreground",
          className,
        )}
      >
        {emptyPlaceholder}
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={cn("rounded-md border border-input bg-background p-3 text-sm", className)}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="mb-2 mt-4 text-2xl font-bold first:mt-0">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mb-2 mt-4 text-xl font-semibold first:mt-0">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="mb-1.5 mt-3 text-lg font-semibold first:mt-0">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="mb-1.5 mt-3 text-base font-semibold first:mt-0">{children}</h4>
          ),
          p: ({ children }) => <p className="my-2 leading-6">{children}</p>,
          ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-6">{children}</ul>,
          ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-6">{children}</ol>,
          li: ({ children }) => <li className="leading-6">{children}</li>,
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-wander-orange underline underline-offset-2 hover:opacity-80"
            >
              {children}
            </a>
          ),
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          code: ({ children, className: cls }) => {
            // Inline code (no language class) vs block code (fenced).
            const isBlock = typeof cls === "string" && cls.includes("language-");
            if (isBlock) {
              return (
                <code className="block overflow-x-auto whitespace-pre rounded bg-muted p-3 font-mono text-xs">
                  {children}
                </code>
              );
            }
            return (
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{children}</code>
            );
          },
          pre: ({ children }) => <pre className="my-3">{children}</pre>,
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-4 border-wander-orange/50 pl-3 italic text-muted-foreground">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-4 border-border" />,
          table: ({ children }) => (
            <table className="my-3 w-full border-collapse text-xs">{children}</table>
          ),
          th: ({ children }) => (
            <th className="border border-border bg-muted px-2 py-1 text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => <td className="border border-border px-2 py-1">{children}</td>,
        }}
      >
        {value}
      </ReactMarkdown>
    </div>
  );
}
